/* Rebuy owns bundle choices, prices, variants and the single add action. */
(() => {
  const selector = '[data-tsf-product-fbt]';
  const states = new WeakMap();
  const connected = new Set();
  const actionSelector = '.rebuy-bundle__actions-buttons button.rebuy-button';
  const busyAttribute = 'data-tsf-fbt-busy';
  const loading = text => /^adding(?:\s*(?:\.{3}|…))?$/i.test(text.trim());

  function restoreActions(state) {
    state.guarded.forEach((original, button) => {
      for (const name of ['aria-disabled', 'aria-busy']) {
        if (button.getAttribute(name) !== 'true') continue;
        if (original[name] === null) button.removeAttribute(name);
        else button.setAttribute(name, original[name]);
      }
      button.removeAttribute(busyAttribute);
    });
    state.guarded.clear();
  }

  function guardActions(region, records = []) {
    const state = states.get(region);
    if (!state?.adding) return;
    const buttons = [...region.querySelectorAll(`.rebuy-widget.widget-type-dynamic-bundle.is-visible ${actionSelector}`)];
    // Read the native label, including transitions that completed before the
    // observer ran. A product-level rebuy.add event can precede bundle completion.
    for (const record of records) {
      const element = record.target.nodeType === Node.ELEMENT_NODE ? record.target : record.target.parentElement;
      const button = element?.closest(actionSelector);
      if (!state.guarded.has(button) && !buttons.includes(button)) continue;
      if (loading(record.oldValue || '') || [...record.removedNodes].some(node => loading(node.textContent || ''))) {
        state.loadingButtons.add(button);
      }
    }
    buttons.forEach(button => { if (loading(button.textContent)) state.loadingButtons.add(button); });
    // A replacement may initially render ready while the old request is still
    // pending. Only the same native action's witnessed loading-to-ready change
    // completes it; absence of a loading label on a new node is not completion.
    if (buttons.some(button => state.loadingButtons.has(button) && !loading(button.textContent)) &&
      buttons.every(button => !loading(button.textContent))) {
      state.adding = false;
      state.loadingButtons.clear();
      restoreActions(state);
      return;
    }
    buttons.forEach(button => {
      if (!state.guarded.has(button)) {
        // Native rerenders can clone our attributes. Carry the pre-guard values
        // with the node so a clone does not retain theme-owned busy semantics.
        let original;
        try { original = JSON.parse(button.getAttribute(busyAttribute)); } catch { /* Native fresh node. */ }
        if (!original || !['aria-disabled', 'aria-busy'].every(name => original[name] === null || typeof original[name] === 'string')) {
          original = { 'aria-disabled': button.getAttribute('aria-disabled'), 'aria-busy': button.getAttribute('aria-busy') };
        }
        state.guarded.set(button, original);
        button.setAttribute(busyAttribute, JSON.stringify(original));
      }
      // Keep native availability and its first click/default keyboard action
      // intact. Capture enforcement supplies the disabled behavior while busy.
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
    });
  }

  function captureAction(region, event) {
    const target = event.target instanceof Element && event.target.closest(actionSelector);
    if (!target || !target.closest('.rebuy-widget.widget-type-dynamic-bundle.is-visible') || !region.contains(target)) return;
    if (event.type !== 'click' && !['Enter', ' ', 'Spacebar'].includes(event.key)) return;
    const state = states.get(region);
    if (state.adding) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    // Enter/Space must generate their first native click before we latch.
    if (event.type !== 'click' || target.disabled) return;
    state.adding = true;
    state.loadingButtons.clear();
    guardActions(region);
  }

  function disconnect(region) {
    const state = states.get(region);
    if (!state) return;
    ++state.generation;
    state.observer.disconnect();
    for (const type of ['click', 'keydown', 'keyup']) region.removeEventListener(type, state.capture, true);
    region.removeEventListener('change', state.onChange);
    restoreActions(state);
    states.delete(region);
    connected.delete(region);
    delete region.dataset.tsfFbtBound;
  }

  function variantFromHref(href) {
    return /[?&]variant=(\d+)/.exec(href || '')?.[1] || '';
  }

  function productHandle(card) {
    const href = card.querySelector('.rebuy-product-title-link')?.getAttribute('href') || '';
    const match = /\/products\/([^/?#]+)/.exec(href);
    if (!match) return '';
    try { return decodeURIComponent(match[1]); } catch { return ''; }
  }

  // The Build Your Own Box is an empty container until its contents are
  // chosen on its own page, so it must never be offered or added from this rail.
  function excludedHandles(region) {
    return (region.dataset.tsfFbtExclude || 'thinslim-build-your-own-box').split(',').map(handle => handle.trim()).filter(Boolean);
  }

  // Hide excluded rows and clear their selection so Rebuy leaves them out of the
  // Add All request and its total. Bounded so a native re-check cannot loop.
  function excludeRows(region, widget) {
    const state = states.get(region);
    const handles = excludedHandles(region);
    // Rebuy can reuse a row node for a different product, so the exclusion is
    // recomputed from the row's current handle every time.
    const mark = (element, excluded) => {
      if (excluded) element.setAttribute('data-tsf-fbt-excluded', '');
      else element.removeAttribute('data-tsf-fbt-excluded');
      element.hidden = excluded;
    };
    widget?.querySelectorAll('.rebuy-bundle__image').forEach(image => {
      const match = /\/products\/([^/?#]+)/.exec(image.querySelector('a')?.getAttribute('href') || '');
      mark(image, Boolean(match && handles.includes(match[1])));
    });
    widget?.querySelectorAll('.rebuy-bundle__items .rebuy-product-block').forEach(card => {
      const excluded = handles.includes(productHandle(card));
      mark(card, excluded);
      if (!excluded) return;
      const checkbox = card.querySelector('input.rebuy-checkbox');
      if (!checkbox?.checked) {
        // The box is unselected: the click budget starts over for later rerenders.
        state.exclusionClicks = 0;
        return;
      }
      if (state.exclusionClicks < 5) {
        state.exclusionClicks += 1;
        checkbox.click();
      }
    });
  }

  function requestProductImages(region, handle) {
    const state = states.get(region);
    if (state.requests.has(handle) || typeof fetch !== 'function') return;
    const request = fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' })
      .then(response => { if (!response.ok) throw new Error('Product images unavailable'); return response.json(); })
      .then(product => {
        const images = new Map();
        for (const variant of product.variants || []) {
          const featured = variant.featured_image;
          const src = featured?.src || (product.images?.length === 1 ? product.images[0] : null);
          if (src) images.set(String(variant.id), { src, alt: featured?.alt || '' });
        }
        state.productImages.set(handle, images);
        if (states.get(region) === state && region.isConnected) {
          syncCardImages(region, region.querySelector('.rebuy-widget.widget-type-dynamic-bundle.is-visible'));
        }
      })
      .catch(() => { /* Keep an unavailable variant image hidden rather than show the wrong flavor. */ });
    state.requests.set(handle, request);
  }

  function syncSourceVariant(region) {
    const state = states.get(region);
    if (!state?.pendingVariant) return;
    const sourceSelect = [...region.querySelectorAll('.rebuy-product-block.is-input-product select.rebuy-select')]
      .find(select => select.closest('.rebuy-product-block')?.classList.contains(`product-id-${region.dataset.tsfFbtProductId}`));
    const option = sourceSelect && [...sourceSelect.options]
      .find(option => option.value === state.pendingVariant && !option.disabled);
    if (!option || sourceSelect.disabled) return;
    // Consume before dispatch: Rebuy may synchronously change its markup. The
    // published native template owns variant selection through this change event.
    const variantId = state.pendingVariant;
    state.pendingVariant = '';
    if (sourceSelect.value === variantId) return;
    sourceSelect.value = variantId;
    sourceSelect.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function syncCardImages(region, widget) {
    if (!widget) return;
    const state = states.get(region);
    widget.querySelectorAll('.rebuy-bundle__images .rebuy-bundle__image').forEach(source => {
      const productClass = [...source.classList].find(name => /^product-id-\d+$/.test(name));
      const image = source.querySelector('img');
      if (productClass && image?.getAttribute('src')) {
        const src = image.getAttribute('src');
        const previous = state.images.get(productClass);
        const selected = widget.querySelector(`.rebuy-bundle__items .${productClass} select.rebuy-select`)?.value || '';
        const variantId = variantFromHref(source.querySelector('a')?.getAttribute('href')) ||
          (previous?.src === src ? previous.variantId : selected);
        const data = { src, alt: image.getAttribute('alt') || '', variantId };
        state.images.set(productClass, data);
        if (variantId) state.variantImages.set(`${productClass}:${variantId}`, data);
      }
    });
    const cards = [...widget.querySelectorAll('.rebuy-bundle__items .rebuy-product-block')];
    cards.forEach(card => {
      const productClass = [...card.classList].find(name => /^product-id-\d+$/.test(name));
      const variantId = card.querySelector('select.rebuy-select')?.value || variantFromHref(card.querySelector('.rebuy-product-title-link')?.getAttribute('href'));
      const handle = productHandle(card);
      const native = state.images.get(productClass);
      const data = state.variantImages.get(`${productClass}:${variantId}`) ||
        state.productImages.get(handle)?.get(variantId) ||
        (native && (!variantId || native.variantId === variantId) ? native : null);
      if (!data && handle && variantId) requestProductImages(region, handle);
      if (!data && !native) return;
      let media = card.querySelector(':scope > .tsf-product-fbt__media');
      if (!media) {
        media = document.createElement('div');
        media.className = 'tsf-product-fbt__media';
        media.append(document.createElement('img'));
        card.prepend(media);
      }
      const image = media.querySelector('img');
      image.style.visibility = data ? '' : 'hidden';
      if (!data) return;
      const alt = data.alt || card.querySelector('.rebuy-product-title-link')?.textContent.trim() || '';
      for (const [name, value] of Object.entries({ src: data.src, alt })) {
        if (image.getAttribute(name) !== value) image.setAttribute(name, value);
      }
    });
    // Retain native images as a fallback until every card has its own image.
    // Cached images stay with unchecked rows when Rebuy removes its image strip.
    region.toggleAttribute('data-tsf-fbt-cards', Boolean(cards.length) && cards.every(card => card.querySelector(':scope > .tsf-product-fbt__media img')));
  }

  function settle(region, records = []) {
    guardActions(region, records);
    const widget = region.querySelector('.rebuy-widget.widget-type-dynamic-bundle.is-visible');
    excludeRows(region, widget);
    syncCardImages(region, widget);
    const bundle = widget?.querySelector('.rebuy-bundle');
    const items = bundle?.querySelector(':scope > .rebuy-bundle__items');
    const actions = bundle?.querySelector(':scope > .rebuy-bundle__actions');
    // Move the existing native node so keyboard and reading order follow the
    // choices. Rebuy retains its button handlers and may replace it on rerender.
    if (items && actions && (items.compareDocumentPosition(actions) & Node.DOCUMENT_POSITION_PRECEDING)) {
      items.after(actions);
    }
    if (widget?.hasAttribute('inert')) widget.removeAttribute('inert');
    widget?.querySelectorAll('.rebuy-product-info > h5.rebuy-product-title').forEach(heading => {
      heading.setAttribute('role', 'heading');
      heading.setAttribute('aria-level', '3');
    });
    const recommendations = widget ? [...widget.querySelectorAll('.rebuy-bundle__items .rebuy-product-block')].filter(card => {
      const link = card.querySelector('a.rebuy-product-title-link[href*="/products/"]');
      const match = /\/products\/([^/?#]+)/.exec(link?.getAttribute('href') || '');
      if (!match || !card.querySelector('input.rebuy-checkbox')) return false;
      let handle;
      try { handle = decodeURIComponent(match[1]); } catch { return false; }
      if (handle === region.dataset.tsfFbtSource || excludedHandles(region).includes(handle)) return false;
      // Rebuy removes selected images and its Add All area when every item is
      // unchecked. Keep the actual recommendation rows available for reselection.
      return /(?:^|\s)product-id-\d+(?:\s|$)/.test(card.className);
    }) : [];
    region.toggleAttribute('data-tsf-fbt-filled', Boolean(recommendations.length));
    syncSourceVariant(region);
  }

  function bind() {
    document.querySelectorAll(selector).forEach(region => {
      if (region.dataset.tsfFbtBound) return;
      region.dataset.tsfFbtBound = '1';
      const state = {
        pendingVariant: region.dataset.tsfFbtVariantId || '', generation: 0,
        adding: false, exclusionClicks: 0, loadingButtons: new Set(), guarded: new Map(), images: new Map(),
        variantImages: new Map(), productImages: new Map(), requests: new Map(),
        capture: event => captureAction(region, event),
        onChange: event => {
          if (event.target.matches('select.rebuy-select')) queueMicrotask(() => settle(region));
        },
        observer: new MutationObserver(records => settle(region, records)),
      };
      states.set(region, state);
      connected.add(region);
      for (const type of ['click', 'keydown', 'keyup']) region.addEventListener(type, state.capture, true);
      region.addEventListener('change', state.onChange);
      settle(region);
      state.observer.observe(region, {
        childList: true,
        subtree: true,
        characterData: true,
        characterDataOldValue: true,
        attributes: true,
        attributeFilter: ['class', 'inert', 'src', 'href'],
      });
    });
  }

  bind();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind, { once: true });
  document.addEventListener('shopify:section:load', bind);
  document.addEventListener('shopify:section:unload', event => {
    connected.forEach(region => {
      if (event.target instanceof Element && event.target.contains(region)) disconnect(region);
    });
  });
  // An ancestor can be removed without a Shopify section event. Drop the latch
  // and listeners so a detached native node can safely be inserted and rebound.
  new MutationObserver(() => {
    connected.forEach(region => { if (!region.isConnected) disconnect(region); });
  }).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('shopify:product:select', event => {
    if (!(event.target instanceof Element) || !event.promise?.then) return;
    if (event.target.closest('product-card, .product-card, quick-add-component, quick-add-dialog')) return;
    const grid = event.target.closest('product-component[data-product-grid-content]');
    const owner = grid?.closest('.product-information');
    if (!owner || owner.querySelector('product-component[data-product-grid-content]') !== grid) return;
    const region = [...owner.querySelectorAll(selector)].find(region => region.closest('.product-information') === owner);
    const state = region && states.get(region);
    if (!state) return;
    const pickerProductId = event.target.closest('variant-picker')?.dataset.productId;
    if (pickerProductId && pickerProductId !== region.dataset.tsfFbtProductId) return;
    const generation = ++state.generation;
    state.pendingVariant = '';
    event.promise.then(({ detail }) => {
      if (!region.isConnected || generation !== state.generation) return;
      if (String(detail?.productId || '') !== region.dataset.tsfFbtProductId) return;
      const variantId = String(detail?.resource?.id || '');
      if (!/^\d+$/.test(variantId)) return;
      state.pendingVariant = variantId;
      syncSourceVariant(region);
    }).catch(() => {
      // Aborted and failed theme requests must leave Rebuy's current choice alone.
    });
  });
})();
